import {LitElement, html, css} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import {tailwindStyles} from '../utils/tailwind-styles.js';
import {environment} from '../environments/environment.js';
import './header-nav.js';
import './footer-section.js';
import './toggle-switch.js';
import './pricing-chart-section.js';
import {contactService} from '../services/contact.service.js';
import type {ContactFormData} from '../types/contact.js';

/**
 * Landing Page Component
 * Complete Forward Compute landing page with hero, features, and contact sections
 */
@customElement('landing-page')
export class LandingPage extends LitElement {
  // Disable Shadow DOM to use global Tailwind styles
  protected createRenderRoot() {
    return this;
  }

  @state()
  private _formData: ContactFormData = {
    firstname: '',
    lastname: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    agreeToPrivacy: false
  };

  @state()
  private _isSubmitting = false;

  @state()
  private _submitStatus: 'idle' | 'success' | 'error' = 'idle';

  @state()
  private _errorMessage = '';

  @state()
  private _validationErrors: Partial<Record<keyof ContactFormData, string>> = {};

  private async _handleFormSubmit(e: Event) {
    e.preventDefault();

    // Reset status
    this._submitStatus = 'idle';
    this._errorMessage = '';

    // Validate form
    if (!this._validateForm()) {
      return;
    }

    // Set loading state
    this._isSubmitting = true;

    try {
      // Prepare request data (exclude agreeToPrivacy)
      const {agreeToPrivacy, ...requestData} = this._formData;

      // Convert empty strings to null for optional fields
      const payload = {
        ...requestData,
        company: requestData.company || null,
        phone: requestData.phone || null,
      };

      // Submit to API
      await contactService.submitContact(payload);

      // Success
      this._submitStatus = 'success';

      // Clear form after successful submission
      this._resetForm();

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        if (this._submitStatus === 'success') {
          this._submitStatus = 'idle';
        }
      }, 5000);
    } catch (error) {
      // Error
      this._submitStatus = 'error';
      this._errorMessage = error instanceof Error
        ? error.message
        : 'Failed to submit form. Please try again.';

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        if (this._submitStatus === 'error') {
          this._submitStatus = 'idle';
        }
      }, 5000);
    } finally {
      this._isSubmitting = false;
    }
  }

  private _validateForm(): boolean {
    const errors: Partial<Record<keyof ContactFormData, string>> = {};

    // Required fields validation
    if (!this._formData.firstname.trim()) {
      errors.firstname = 'First name is required';
    }

    if (!this._formData.lastname.trim()) {
      errors.lastname = 'Last name is required';
    }

    if (!this._formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      // Email format validation
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(this._formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (!this._formData.message.trim()) {
      errors.message = 'Message is required';
    }

    if (!this._formData.agreeToPrivacy) {
      errors.agreeToPrivacy = 'You must agree to the privacy policy';
    }

    this._validationErrors = errors;
    return Object.keys(errors).length === 0;
  }

  private _resetForm(): void {
    this._formData = {
      firstname: '',
      lastname: '',
      company: '',
      email: '',
      phone: '',
      message: '',
      agreeToPrivacy: false
    };
    this._validationErrors = {};
  }

  private _handleInputChange(field: string, value: string | boolean) {
    this._formData = {...this._formData, [field]: value};
  }

  private _scrollToContact() {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  render() {
    return html`
      <div class="min-h-screen bg-surface-base">
        <!-- Header -->
        <header-nav></header-nav>

        <!-- Hero Section -->
        <section class="pt-24 pb-16 lg:pt-32 lg:pb-24 px-4 lg:px-16">
          <div class="container mx-auto">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <!-- Left: Content -->
              <div class="flex flex-col gap-10 text-center lg:text-left">
                <h1 class="font-serif text-4xl md:text-5xl lg:text-6xl text-text-primary leading-tight">
                  The platform for any<br>
                  <span class="text-text-secondary">AI compute</span> needs
                </h1>

                <p class="text-text-primary text-base md:text-lg max-w-xl mx-auto lg:mx-0">
                  Deploy high-performance GPUs worldwide in minutes,<br class="hidden md:block">
                  while extracting significant cost savings
                </p>

                <div class="flex justify-center lg:justify-start">
                  <button
                    @click="${this._scrollToContact}"
                    class="px-5 py-3 text-white font-sans text-sm hover:opacity-80 transition-opacity duration-200"
                    style="background-color: rgba(85, 123, 213, 0.3);"
                  >
                    Talk to us now
                  </button>
                </div>
              </div>

              <!-- Right: Illustration -->
              <div class="relative w-full h-full flex items-center justify-center">
                <svg width="536" height="581" viewBox="0 0 536 581" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-auto max-w-[268px] lg:max-w-[536px]">
                  <path d="M0 0H100L274 285.5L100 581H0L174 285.5L0 0Z" fill="url(#paint0_linear_497_1297)"/>
                  <path d="M262 0H362L536 285.5L362 581H262L436 285.5L262 0Z" fill="url(#paint1_linear_497_1297)"/>
                  <path d="M262 0H362L536 285.5L362 581H262L436 285.5L262 0Z" fill="url(#paint2_linear_497_1297)"/>
                  <defs>
                    <linearGradient id="paint0_linear_497_1297" x1="229" y1="284" x2="56.5" y2="8.09617e-06" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#557BD5"/>
                      <stop offset="0.5258" stop-color="#001C64"/>
                      <stop offset="1" stop-color="#000616"/>
                    </linearGradient>
                    <linearGradient id="paint1_linear_497_1297" x1="491" y1="284" x2="314.5" y2="43" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#557BD5"/>
                      <stop offset="0.5258" stop-color="#001C64"/>
                      <stop offset="1" stop-color="#000616"/>
                    </linearGradient>
                    <linearGradient id="paint2_linear_497_1297" x1="469.5" y1="310" x2="310.5" y2="581" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#557BD5"/>
                      <stop offset="0.438544" stop-color="#001C64"/>
                      <stop offset="1" stop-color="#000616"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </section>

        <!-- Features Section -->
        <section class="py-16 px-4 lg:px-16">
          <div class="container mx-auto">
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 md:gap-12 xl:gap-28">
              <!-- GPU Marketplace -->
              <div class="space-y-4 md:space-y-6">
                <!-- Icon + Title -->
                <div class="flex items-center gap-3">
                  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 flex-shrink-0">
                    <path d="M1.40909 9.59091H5.95455M5.95455 9.59091V6.86364M5.95455 9.59091H10.5M10.5 9.59091V6.86364M10.5 9.59091H15.0455M15.0455 9.59091V6.86364M15.0455 9.59091H19.5909M7.77273 13.2273H9.59091M11.4091 13.2273H13.2273M11.4091 15.9545H13.2273M7.77273 15.9545H9.59091M2.31818 20.5V9.59091H0.5V5.95455L3.22727 0.5H17.7727L20.5 5.95455V9.59091H18.6818V20.5H2.31818Z" stroke="white" stroke-linejoin="round"/>
                  </svg>
                  <h3 class="text-text-primary font-sans font-semibold text-base md:text-lg leading-tight">GPU Marketplace</h3>
                </div>
                <!-- Description with gradient border aligned with icon -->
                <div class="flex gap-3">
                  <div class="w-5 flex-shrink-0 border-l-gradient"></div>
                  <p class="text-text-secondary text-sm md:text-base lg:text-lg leading-normal">
                    Instant access to worldwide GPU compute, accessible within minutes, at competitive prices
                  </p>
                </div>
              </div>

              <!-- Compute Auctions -->
              <div class="space-y-4 md:space-y-6">
                <!-- Icon + Title -->
                <div class="flex items-center gap-3">
                  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 flex-shrink-0">
                    <path d="M7.14539 12.3283L8.29012 11.1836L9.81642 12.7099L8.67169 13.8546M7.14539 12.3283L8.67169 13.8546M7.14539 12.3283C7.09537 12.278 7.03591 12.2382 6.97043 12.2109C6.90494 12.1837 6.83473 12.1697 6.76381 12.1697C6.6929 12.1697 6.62268 12.1837 6.5572 12.2109C6.49172 12.2382 6.43226 12.278 6.38224 12.3283L0.658603 18.0519C0.608337 18.102 0.568447 18.1614 0.54123 18.2269C0.514012 18.2924 0.500001 18.3626 0.500001 18.4335C0.500001 18.5044 0.514012 18.5746 0.54123 18.6401C0.568447 18.7056 0.608337 18.7651 0.658603 18.8151L2.1849 20.3414C2.23493 20.3917 2.29439 20.4316 2.35987 20.4588C2.42535 20.486 2.49557 20.5 2.56648 20.5C2.6374 20.5 2.70761 20.486 2.77309 20.4588C2.83858 20.4316 2.89804 20.3917 2.94806 20.3414L8.67169 14.6178C8.72196 14.5677 8.76185 14.5083 8.78906 14.4428C8.81628 14.3773 8.83029 14.3071 8.83029 14.2362C8.83029 14.1653 8.81628 14.0951 8.78906 14.0296C8.76185 13.9641 8.72196 13.9046 8.67169 13.8546M9.24406 13.2822L7.71775 11.7559M17.829 9.2757L11.7238 3.17049L6.00012 8.89413L12.1053 14.9993L17.829 9.2757ZM20.5 11.1836L18.2105 8.89413L11.7238 15.3809L14.0132 17.6704L20.5 11.1836ZM12.1059 2.78945L9.81642 0.5L3.32963 6.98679L5.61909 9.27624L12.1059 2.78945Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <h3 class="text-text-primary font-sans font-semibold text-base md:text-lg leading-tight">Compute Auctions</h3>
                </div>
                <!-- Description with gradient border aligned with icon -->
                <div class="flex gap-3">
                  <div class="w-5 flex-shrink-0 border-l-gradient"></div>
                  <p class="text-text-secondary text-sm md:text-base lg:text-lg leading-normal">
                    Periodic auctions to procure high-performance compute from reputable AI clouds
                  </p>
                </div>
              </div>

              <!-- Compute Concierge -->
              <div class="space-y-4 md:space-y-6">
                <!-- Icon + Title -->
                <div class="flex items-center gap-3">
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-auto flex-shrink-0">
                    <path d="M7.85714 0V1.47368H12.1429V0H7.85714ZM10 2.21053C5.50214 2.21053 1.83857 5.77684 1.47286 10.3158H0V14H20V10.3158H18.5264C18.1621 5.77684 14.4979 2.21053 10 2.21053ZM10 3.68421C11.7726 3.6742 13.4846 4.34948 14.7986 5.57697C16.1125 6.80447 16.9331 8.49514 17.0986 10.3158H2.90214C3.25357 6.56821 6.26643 3.68421 10 3.68421ZM1.42857 11.7895H18.5714V12.5263H1.42857V11.7895Z" fill="white"/>
                  </svg>
                  <h3 class="text-text-primary font-sans font-semibold text-base md:text-lg leading-tight">Compute Concierge</h3>
                </div>
                <!-- Description with gradient border aligned with icon -->
                <div class="flex gap-3">
                  <div class="w-5 flex-shrink-0 border-l-gradient"></div>
                  <p class="text-text-secondary text-sm md:text-base lg:text-lg leading-normal">
                    Can't find what you need? Talk to our expert to procure enterprise-grade compute fitted to your needs
                  </p>
                </div>
              </div>

              <!-- Derivatives Exchange -->
              <div class="space-y-4 md:space-y-6">
                <!-- Icon + Title -->
                <div class="flex items-center gap-3">
                  <svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-auto flex-shrink-0">
                    <path d="M20 0V6.0141H18.7482V2.05795L11.2372 9.27486L7.48166 5.66641L0.880196 12L0 11.1543L7.48166 3.95615L11.2372 7.5646L17.8582 1.20282H13.7408V0H20Z" fill="white"/>
                  </svg>
                  <h3 class="text-text-primary font-sans font-semibold text-base md:text-lg leading-tight">Derivatives Exchange</h3>
                </div>
                <!-- Description with gradient border aligned with icon -->
                <div class="flex gap-3">
                  <div class="w-5 flex-shrink-0 border-l-gradient"></div>
                  <p class="text-text-secondary text-sm md:text-base lg:text-lg leading-normal">
                    We're developing the first worldwide market to exchange compute assets, from spot instances to long-term compute procurements
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Pricing Chart Section -->
        ${environment.features?.enablePricingChart
          ? html`<pricing-chart-section></pricing-chart-section>`
          : ''}

        <!-- Contact Section -->
        <section id="contact-section" class="py-16 px-4 lg:px-16">
          <div class="container mx-auto max-w-2xl">
            <div class="text-center mb-8">
              <h2 class="font-serif text-3xl md:text-4xl text-text-primary mb-4">Contact us</h2>
              <p class="text-text-secondary md:text-lg">Have questions? We're here to help.</p>
            </div>

            <form @submit="${this._handleFormSubmit}" class="space-y-6">
              <!-- First Name (2 columns) -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-text-primary text-sm mb-2">First name *</label>
                  <input
                    type="text"
                    .value="${this._formData.firstname}"
                    class="w-full px-4 py-3 bg-transparent border ${this._validationErrors.firstname ? 'border-red-500' : 'border-surface-border'} text-text-primary text-sm focus:outline-none focus:border-brand-primary"
                    @input="${(e: Event) => this._handleInputChange('firstname', (e.target as HTMLInputElement).value)}"
                  />
                </div>
                <div>
                  <label class="block text-text-primary text-sm mb-2">Last name *</label>
                  <input
                    type="text"
                    .value="${this._formData.lastname}"
                    class="w-full px-4 py-3 bg-transparent border ${this._validationErrors.lastname ? 'border-red-500' : 'border-surface-border'} text-text-primary text-sm focus:outline-none focus:border-brand-primary"
                    @input="${(e: Event) => this._handleInputChange('lastname', (e.target as HTMLInputElement).value)}"
                  />
                </div>
              </div>

              <!-- Company -->
              <div>
                <label class="block text-text-primary text-sm mb-2">Company</label>
                <input
                  type="text"
                  .value="${this._formData.company}"
                  class="w-full px-4 py-3 bg-transparent border border-surface-border text-text-primary text-sm focus:outline-none focus:border-brand-primary"
                  @input="${(e: Event) => this._handleInputChange('company', (e.target as HTMLInputElement).value)}"
                />
              </div>

              <!-- Email -->
              <div>
                <label class="block text-text-primary text-sm mb-2">Email *</label>
                <input
                  type="email"
                  .value="${this._formData.email}"
                  class="w-full px-4 py-3 bg-transparent border ${this._validationErrors.email ? 'border-red-500' : 'border-surface-border'} text-text-primary text-sm focus:outline-none focus:border-brand-primary"
                  @input="${(e: Event) => this._handleInputChange('email', (e.target as HTMLInputElement).value)}"
                />
              </div>

              <!-- Phone Number -->
              <div>
                <label class="block text-text-primary text-sm mb-2">Phone number</label>
                <input
                  type="tel"
                  .value="${this._formData.phone}"
                  class="w-full px-4 py-3 bg-transparent border border-surface-border text-text-primary text-sm focus:outline-none focus:border-brand-primary"
                  @input="${(e: Event) => this._handleInputChange('phone', (e.target as HTMLInputElement).value)}"
                />
              </div>

              <!-- Message -->
              <div>
                <label class="block text-text-primary text-sm mb-2">Message *</label>
                <textarea
                  rows="4"
                  .value="${this._formData.message}"
                  class="w-full px-4 py-3 bg-transparent border ${this._validationErrors.message ? 'border-red-500' : 'border-surface-border'} text-text-primary text-sm resize-none focus:outline-none focus:border-brand-primary"
                  @input="${(e: Event) => this._handleInputChange('message', (e.target as HTMLTextAreaElement).value)}"
                ></textarea>
              </div>

              <!-- Privacy Policy Toggle -->
              <div>
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0">
                    <toggle-switch
                      .checked="${this._formData.agreeToPrivacy}"
                      @toggle-change="${(e: CustomEvent) => this._handleInputChange('agreeToPrivacy', e.detail.checked)}"
                      ariaLabel="Agree to privacy policy"
                    ></toggle-switch>
                  </div>
                  <label class="text-sm cursor-pointer flex-1 ${this._validationErrors.agreeToPrivacy ? 'text-red-400' : 'text-text-primary'}" @click="${() => this._handleInputChange('agreeToPrivacy', !this._formData.agreeToPrivacy)}">
                    By selecting this, you agree to our <span class="font-semibold cursor-pointer">privacy policy</span> *
                    ${this._validationErrors.agreeToPrivacy ? html`
                      <span class="text-red-400 text-xs ml-2">(Required)</span>
                    ` : ''}
                  </label>
                </div>
              </div>

              <!-- Submit Button -->
              <div>
                <button
                  type="submit"
                  ?disabled="${this._isSubmitting || this._submitStatus === 'success'}"
                  class="w-full px-6 py-3 font-sans text-sm transition-all duration-300 disabled:cursor-not-allowed
                    ${this._submitStatus === 'success'
                      ? 'bg-green-800 text-white'
                      : this._submitStatus === 'error'
                        ? 'bg-red-700 text-white hover:bg-red-800'
                        : 'bg-surface-elevated text-text-primary hover:bg-surface-overlay'}"
                >
                  ${this._submitStatus === 'success'
                    ? 'Message Sent'
                    : this._submitStatus === 'error'
                      ? 'Something went wrong, please try again'
                      : this._isSubmitting
                        ? 'Sending...'
                        : 'Let\'s talk'}
                </button>
              </div>
            </form>
          </div>
        </section>

        <!-- Footer -->
        <footer-section></footer-section>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landing-page': LandingPage;
  }
}
